/**
 * The "Find creators" form body, rendered from the search schema: platform
 * and mode, the query box, the always-open groups, then the collapsed
 * advanced groups, an active-filters line, and a sticky action bar with
 * Saved searches beside Search.
 */
import type { FormEvent, JSX, ReactNode } from 'react';
import { History, Info, Loader2, RotateCcw, Search as SearchIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ActiveFilters } from '@/components/ui/active-filters';
import { PlatformIcon } from '@/components/PlatformIcon';
import { ScheduleToggle } from '../components/SchedulePanel';
import type { SavedSearch, SearchHistoryEntry, SearchParamValue, SearchSchema } from '../api/types';
import { FieldError, Segmented } from './controls';
import { FilterGroups, GroupFields } from './FilterGroups';
import { QueryInput } from './QueryInput';
import { SavedSearchesMenu } from './SavedSearchesMenu';
import { emptyValue, modesFor, type ActiveFilterInfo, type SearchFormState } from './params';
import { ParamsLabel } from './ParamsLabel';
import { toPlatform, type Platform } from '../lib/platform';

export interface SearchFormProps {
  schema: SearchSchema;
  isFallback: boolean;
  state: SearchFormState;
  onStateChange: (next: SearchFormState) => void;
  onParamChange: (key: string, v: SearchParamValue) => void;
  onPlatformChange: (p: Platform) => void;
  errors: Record<string, string>;
  queryError: string | null;
  otherErrors: string[];
  forceOpen: Set<string>;
  active: ActiveFilterInfo[];
  disabled: Record<string, string>;
  addons: Record<string, ReactNode>;
  folderSeeds: string[];
  repeat: { enabled: boolean; everyDays: string };
  onRepeatChange: (r: { enabled: boolean; everyDays: string }) => void;
  /** A schedule in this folder with the same name, which this search would update. */
  repeatUpdates?: string | null;
  history: SearchHistoryEntry[];
  onLoadHistory: (e: SearchHistoryEntry) => void;
  /** Rendered under recent searches (the folder's schedules). */
  aside?: ReactNode;
  onApplySaved: (s: SavedSearch) => void;
  savedParams: () => Record<string, unknown>;
  suggestedName: string;
  canSave: boolean;
  onReset: () => void;
  onClearFilters: () => void;
  onSubmit: (ev: FormEvent) => void;
  submitting: boolean;
  submitError: string | null;
}

export function SearchForm(props: SearchFormProps): JSX.Element {
  const { schema, state, errors, active } = props;
  const platforms = schema.platforms.filter((p) => p.available);
  const modes = modesFor(schema, state.platform);
  const mode = modes.find((m) => m.id === state.mode) ?? modes[0];
  const filterDefs = new Map(schema.filters.map((f) => [f.key, f]));

  return (
    <form onSubmit={props.onSubmit} noValidate className="border-t border-border">
      <div className="grid lg:grid-cols-[minmax(0,1fr)_18rem]">
        <div className="min-w-0 space-y-6 p-4 sm:p-5">
          {props.isFallback && (
            <p className="flex items-start gap-2 text-xs text-muted-foreground">
              <Info className="mt-px h-3.5 w-3.5 shrink-0" />
              Using the built-in form: this discovery server doesn't publish its search schema yet.
            </p>
          )}

          <section aria-label="Query" className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              {platforms.length > 1 && (
                <Segmented
                  label="Platform"
                  value={state.platform}
                  onChange={props.onPlatformChange}
                  options={platforms.map((p) => ({
                    value: p.id,
                    label: (
                      <>
                        <PlatformIcon platform={p.id} size={14} />
                        {p.label}
                      </>
                    ),
                  }))}
                />
              )}
              {modes.length > 1 && (
                <Segmented
                  label="Search mode"
                  value={state.mode}
                  onChange={(m) => props.onStateChange({ ...state, mode: m })}
                  options={modes.map((m) => ({ value: m.id, label: m.label }))}
                />
              )}
            </div>
            {mode?.hint && <p className="text-sm text-muted-foreground">{mode.hint}</p>}
            {mode && (
              <QueryInput
                platform={state.platform}
                mode={mode}
                query={state.query}
                sounds={state.sounds}
                onQueryChange={(query) => props.onStateChange({ ...state, query })}
                onSoundsChange={(sounds) => props.onStateChange({ ...state, sounds })}
                error={props.queryError}
                folderSeeds={props.folderSeeds}
                topicTerms={topicWords(state.params.topicTerms)}
              />
            )}
            {/* The "search" group (target, X post options) sits with the query. */}
            <GroupFields
              schema={schema}
              group="search"
              state={state}
              onChange={props.onParamChange}
              errors={errors}
              disabled={props.disabled}
              addons={props.addons}
            />
          </section>

          <FilterGroups
            schema={schema}
            state={state}
            onChange={props.onParamChange}
            errors={errors}
            active={active}
            forceOpen={props.forceOpen}
            disabled={props.disabled}
            addons={props.addons}
            skip={['search']}
          />

          <ScheduleToggle
            enabled={props.repeat.enabled}
            everyDays={props.repeat.everyDays}
            onEnabledChange={(enabled) => props.onRepeatChange({ ...props.repeat, enabled })}
            onDaysChange={(everyDays) => props.onRepeatChange({ ...props.repeat, everyDays })}
            updates={props.repeatUpdates}
          />

          <ActiveFilters
            filters={active.map((a) => ({
              key: a.key,
              label: a.text,
              clear: () => {
                const def = filterDefs.get(a.key);
                if (def) props.onParamChange(a.key, emptyValue(def));
              },
            }))}
            onClearAll={props.onClearFilters}
          />

          {props.otherErrors.map((e) => (
            <FieldError key={e} message={e} />
          ))}
          <FieldError message={props.submitError} />
        </div>

        <aside className="space-y-5 border-t border-border bg-muted/20 p-4 sm:p-5 lg:border-l lg:border-t-0">
          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <History className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
              <h3 className="text-sm font-semibold">Recent searches</h3>
            </div>
            {props.history.length === 0 ? (
              <p className="text-xs leading-relaxed text-muted-foreground">
                Searches you run in this folder show up here, so you can re-use the ones that worked.
              </p>
            ) : (
              <ul className="-mx-1 max-h-[26rem] space-y-0.5 overflow-y-auto">
                {props.history.map((entry) => (
                  <HistoryRow key={entry.id} entry={entry} onLoad={() => props.onLoadHistory(entry)} />
                ))}
              </ul>
            )}
          </div>
          {props.aside}
        </aside>
      </div>

      {/* Sticky so Search is reachable without scrolling back. */}
      <div className="sticky bottom-0 z-10 flex items-center justify-between gap-2 rounded-b-xl border-t border-border bg-card/95 px-3 py-2.5 backdrop-blur-sm sm:gap-3 sm:px-5 sm:py-3">
        <p className="hidden min-w-0 truncate text-sm text-muted-foreground sm:block">
          {active.length > 0
            ? `${active.length} filter${active.length === 1 ? '' : 's'} active`
            : 'No filters: every creator found will be saved'}
        </p>
        <div className="flex flex-1 items-center justify-end gap-2 sm:flex-none">
          <Button type="button" variant="ghost" onClick={props.onReset} aria-label="Reset" title="Reset the form" className="px-3">
            <RotateCcw className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Reset</span>
          </Button>
          <SavedSearchesMenu
            onApply={props.onApplySaved}
            currentParams={props.savedParams}
            suggestedName={props.suggestedName}
            canSave={props.canSave}
          />
          <Button type="submit" disabled={props.submitting}>
            {props.submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <SearchIcon className="h-4 w-4" strokeWidth={2} />}
            {props.submitting ? 'Starting…' : 'Search'}
          </Button>
        </div>
      </div>
    </form>
  );
}

function HistoryRow({ entry, onLoad }: { entry: SearchHistoryEntry; onLoad: () => void }): JSX.Element {
  const p = entry.params as Record<string, unknown>;
  const platform = toPlatform(p.platform);
  const stats = p.stats as { saved?: number } | undefined;
  return (
    <li>
      <button
        type="button"
        onClick={onLoad}
        title="Load these settings into the form"
        className="w-full rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-accent"
      >
        <span className="flex items-center gap-1.5">
          <PlatformIcon platform={platform} size={14} className="shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1 truncate text-sm">
            <ParamsLabel params={p} />
          </span>
        </span>
        <span className="mt-0.5 block pl-5 text-xs text-muted-foreground">
          {stats?.saved != null ? `${stats.saved} found · ` : ''}
          {new Date(entry.ranAt).toLocaleDateString()}
        </span>
      </button>
    </li>
  );
}

/** The form keeps list params as an array or as the raw comma-separated text. */
function topicWords(v: unknown): string[] | undefined {
  if (Array.isArray(v)) return v.map(String).filter(Boolean);
  if (typeof v === 'string' && v.trim()) return v.split(/[,\n]/).map((t) => t.trim()).filter(Boolean);
  return undefined;
}
